import { parseISO, format } from "date-fns";
import { es } from "date-fns/locale";

/**
 * Referencia única para "hoy" y fechas sin desfase (ver .muxi/development/conocimiento-migrado-cursor.md).
 * - Para enviar fecha "solo día" al back: getTodayYMD() o toYMD(date).
 * - Para enviar fecha+hora: getTodayDateTime().
 * - Para cargar fecha desde API: parseDateAsLocalISO(val).
 * - Para mostrar fecha que puede venir en ISO: usar slice(0,10) o formatCustomDateIsoString (dateService).
 */

export function getTodayYMD() {
  return format(new Date(), "yyyy-MM-dd");
}

export function getTodayDateTime() {
  return format(new Date(), "yyyy-MM-dd HH:mm:ss");
}

export function getTodayTime() {
  return format(new Date(), "HH:mm:ss");
}

export function getCurrentYear() {
  return new Date().getFullYear();
}

export function getCurrentMonth() {
  return new Date().getMonth() + 1;
}

export function getLastMonth() {
  return new Date().getMonth();
}

export const formatDateText = (isoString) => {
  const date = parseISO(isoString);

  const dayName = new Intl.DateTimeFormat("es-ES", { weekday: "long" }).format(
    date
  );
  const dayNumber = format(date, "d", { locale: es });

  const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);

  return `${capitalizedDayName} ${dayNumber}`;
};

export const formatMonth = (isoString) => {
  const date = new Date(isoString);

  const monthName = new Intl.DateTimeFormat("es-ES", { month: "long" }).format(
    date
  );

  const capitalizedMonthName =
    monthName.charAt(0).toUpperCase() + monthName.slice(1);

  return capitalizedMonthName;
};

export const formatYear = (isoString) => {
  const date = new Date(isoString);
  const year = date.getFullYear();
  return year.toString();
};

export const getDateObj = () => {
  const fecha = new Date();
  const year = fecha.getFullYear();
  const month = fecha.getMonth() + 1;
  const day = fecha.getDate();
  return { day, month, year };
};

export const formatMonthYear = (date) => {
  if (date === "0000-00-00 00:00:00") return "";
  if (!(date instanceof Date)) {
    date = parseISO(date);
  }

  const month = format(date, "MM");
  const year = format(date, "yyyy");
  const day = format(date, "dd");

  return `${day}/${month}/${year}`;
};

export function formatCustomDateName(fechaCompleta) {
  const day = format(fechaCompleta, "dd");
  const monthIndex = format(fechaCompleta, "MM") - 1;
  const year = format(fechaCompleta, "yyyy");

  return `${day}/${monthsMini[monthIndex]}/${year}`;
}

export function formatCustomDateNameWithHours(fechaCompleta) {
  if (!fechaCompleta) return "";

  const fecha =
    typeof fechaCompleta === "string" ? parseISO(fechaCompleta) : fechaCompleta;

  const dia = format(fecha, "d", { locale: es });
  const mes = format(fecha, "MMMM", { locale: es });
  const anio = format(fecha, "yyyy", { locale: es });
  const hora = format(fecha, "HH:mm", { locale: es });

  const mesCapitalizado = mes.charAt(0).toUpperCase() + mes.slice(1);

  return `${dia} ${mesCapitalizado} ${anio}    ${hora}`;
}

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(date);
};

export const formatDateYMD = (dateString) => {
  const formattedDate = format(new Date(dateString), "yyyy-MM-dd");
  return formattedDate;
};

/** True solo si (year, month) coincide con el mes calendario actual (1–12). */
export const getCurrentMonthYear = (year, month) => {
  const date = new Date();
  const currentMonth = date.getMonth() + 1;
  const currentYear = date.getFullYear();
  const y = Number(year);
  const m = Number(month);
  if (Number.isNaN(y) || Number.isNaN(m)) return false;
  return y === currentYear && m === currentMonth;
};

export const getCurrentMonthYearWithDate = (isoDateString) => {
  if (!isoDateString) return false;

  const date = new Date(isoDateString);
  const currentDate = new Date();

  const year = date.getFullYear();

  const currentYear = currentDate.getFullYear();

  return year === currentYear;
};

export const getMonthName = (monthNumber) => {
  const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre"
  ];
  return months[monthNumber - 1];
};

export const getActualfourMonthPeriod = () => {
  const currentMonth = getCurrentMonth();
  switch (currentMonth) {
    case 1:
    case 2:
    case 3:
    case 4:
      return 1;
    case 5:
    case 6:
    case 7:
    case 8:
      return 2;
    case 9:
    case 10:
    case 11:
    case 12:
      return 3;
    default:
      return 1;
  }
};

export const getFourMonthPeriodName = (period) => {
  switch (period) {
    case 1:
      return ["Enero", "Febrero", "Marzo", "Abril"];
    case 2:
      return ["Mayo", "Junio", "Julio", "Agosto"];
    case 3:
      return ["Septiembre", "Octubre", "Noviembre", "Diciembre"];
    default:
      return ["Enero", "Febrero", "Marzo", "Abril"];
  }
};

export const formatDateWithDayNumber = (isoString) => {
  const date = parseISO(isoString);

  const dayName = format(date, "EEEE", { locale: es });

  const dayNumber = format(date, "d", { locale: es });

  const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);

  return `${capitalizedDayName} ${dayNumber}`;
};

export const removeTimezone = (isoString) => {
  const date = parseISO(isoString);
  return format(date, "yyyy-MM-dd");
};

export const getCurrentAndNextYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return [
    { name: currentYear.toString(), value: currentYear },
    { name: (currentYear + 1).toString(), value: currentYear + 1 }
  ];
};

const monthsMini = [
  "ENE",
  "FEB",
  "MAR",
  "ABR",
  "MAY",
  "JUN",
  "JUL",
  "AGO",
  "SEP",
  "OCT",
  "NOV",
  "DIC"
];

export const parseDateAsLocal = (dateString) => {
  const [year, month, day] = dateString.split("-");
  return new Date(Number(year), Number(month) - 1, Number(day));
};

export function formatCustomDateNameLocal(fechaCompleta) {
  if (!fechaCompleta) return "";

  const fecha = parseDateAsLocal(fechaCompleta); 
  const day = format(fecha, "dd");
  const monthIndex = parseInt(format(fecha, "MM"), 10) - 1;
  const year = format(fecha, "yyyy");

  return `${day}/${monthsMini[monthIndex]}/${year}`;
}

export const toYMD = (val) => {
  if (!val) return "";
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

  if (val instanceof Date && !isNaN(val)) {
    return format(val, "yyyy-MM-dd");
  }

  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
    return val.slice(0, 10);
  }

  const d = new Date(val);
  if (!isNaN(d)) return format(d, "yyyy-MM-dd");

  try { 
    const dl = parseDateAsLocal(val);
    if (!isNaN(dl)) return format(dl, "yyyy-MM-dd");
  } catch {}

  return "";
};

export const parseDateAsLocalISO = (dateLike) => {
  if (!dateLike) return null;

  if (typeof dateLike === "string") {
    const m = dateLike.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
    if (m) {
      const [, y, mo, d] = m;
      return new Date(Number(y), Number(mo) - 1, Number(d));  
    }
    const d2 = new Date(dateLike);
    return isNaN(d2) ? null : new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
  }

  if (dateLike instanceof Date) {
    if (isNaN(dateLike)) return null;
    return new Date(dateLike.getFullYear(), dateLike.getMonth(), dateLike.getDate());
  }

  return null;
};

/**
 * Obtiene año y mes (1-12) de una fecha de importación sin depender de zona horaria.
 * Acepta string ISO (YYYY-MM-DD o con T...) o Date. Devuelve { year, month } o null.
 */
export function getYearMonthFromImportDate(dateLike) {
  if (!dateLike) return null;
  if (typeof dateLike === "string") {
    const m = dateLike.match(/^(\d{4})-(\d{2})/);
    if (m) {
      return { year: Number(m[1]), month: Number(m[2]) };
    }
  }
  if (dateLike instanceof Date && !isNaN(dateLike.getTime())) {
    return {
      year: dateLike.getFullYear(),
      month: dateLike.getMonth() + 1
    };
  }
  return null;
}
